import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { IntakeProvider, useIntake } from '../../contexts/IntakeContext'
import useUserIntakePreferencesFetcher from '../../hooks/useUserIntakePreferencesFetcher'
import { RequestService } from '../../services/RequestService'
import IntakeSleep from './IntakeSleep'
import IntakeCaffeineType from './IntakeCaffeineType'
import IntakeCaffeineTime from './IntakeCaffeineTime'
import { ROUTES } from '../../routes'
import { useSnackbar } from '../../hooks'
import { useTranslation } from "react-i18next"

const Container = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: center;
  padding: 44px;
`

const IntakeSteps = () => {
  const { errorSnackbar } = useSnackbar()
  const [currentStep, setCurrentStep] = useState(0)
  const { data } = useUserIntakePreferencesFetcher()
  const {
    sleepTime,
    setSleepTime,
    setAwakeTime,
    awakeTime,
    caffeineTime,
    selectedCaffeineOptions,
    setCaffeineProducts,
    setCaffeineTime,
  } = useIntake()
  const navigate = useNavigate()
  const { t } = useTranslation('common')

  useEffect(() => {
    if (data) {
      const { sleepTime, awakeTime, caffeineProducts, caffeineTime } = data
      const newSleepTime = new Date()
      const newAwakeTime = new Date()

      if (sleepTime) {
        newSleepTime.setHours(sleepTime.getHours(), sleepTime.getMinutes(), 0)
      }

      if (awakeTime) {
        newAwakeTime.setHours(awakeTime.getHours(), awakeTime.getMinutes(), 0)
      }

      if (sleepTime && awakeTime) {
        if (newSleepTime >= newAwakeTime) {
          setSleepTime(new Date(newSleepTime.getTime() - 1000 * 60 * 60 * 24))
        } else {
          setSleepTime(newSleepTime)
        }

        setAwakeTime(newAwakeTime)
      }

      setCaffeineProducts(caffeineProducts)
      if (caffeineTime) setCaffeineTime(caffeineTime)
    }
  }, [data])

  const handleNext = () => {
    setCurrentStep(prevStep => prevStep + 1)
  }

  const handlePrevious = () => {
    setCurrentStep(prevStep => prevStep - 1)
  }

  const handleFinish = async ({ caffeineTime }) => {
    const response = await RequestService.saveUserIntake({
      sleepTime,
      awakeTime,
      caffeinePreferences: selectedCaffeineOptions,
      caffeineTime,
    })

    if (response.status === RequestService.STATUSES.SUCCESS) {
      navigate(ROUTES.USER_DASHBOARD)
    } else {
      errorSnackbar({ text: response?.data?.message })
    }
  }

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <IntakeSleep
            step={t('caffeine.three_steps.one')}
            onNext={handleNext}
            onPrevious={() => navigate(ROUTES.USER_DASHBOARD)}
            sleepTime={sleepTime}
            setSleepTime={setSleepTime}
            awakeTime={awakeTime}
            setAwakeTime={setAwakeTime}
          />
        )
      case 1:
        return (
          <IntakeCaffeineType
            step={t('caffeine.three_steps.two')}
            onNext={handleNext}
            onPrevious={handlePrevious}
            onFinish={() => handleFinish({ caffeineTime: null })}
          />
        )
      case 2:
        return (
          <IntakeCaffeineTime
            step={t('caffeine.three_steps.three')}
            onNext={() => handleFinish({ caffeineTime })}
            onPrevious={handlePrevious}
          />
        )
      default:
        return null
    }
  }

  return <Container>{renderStep()}</Container>
}

const Intake = () => {
  return (
    <IntakeProvider>
      <IntakeSteps />
    </IntakeProvider>
  )
}

export default Intake
